import React, { useEffect, useMemo, useState } from "react";
import { ActivityIndicator, Pressable, Text, View } from "react-native";
import {
  SafeAreaInsetsContext,
  useSafeAreaInsets,
} from "react-native-safe-area-context";
import { CommonActions } from "@react-navigation/native";
import { Ionicons } from "@expo/vector-icons";
import tw from "twrnc";

import { notify } from "@/components/shared/AppToast";
import { colors } from "@/components/settings/theme";
import { useIsMobileUX } from "@/components/hooks/useIsMobileUX";
import { t, tRich } from "@/localization";
import { switchWorkspace, takeWorkspaceNotice } from "./actions";
import { ROLE_LABELS } from "./roles";
import useWorkspace from "./useWorkspace";

// "You're working in Acme Funeral Home's account": shown above every screen
// on the web (App.js) while you work in someone else's account, with a way
// back to your own. Also shows, once, why the app went back to your own
// account (a removed membership, Teams not on for that account).
//
// `navigation`: a navigation object or the container ref (App.js), used to
// return to the event list after switching.
const WorkspaceBanner = ({ navigationRef, navigation, style }) => {
  const ws = useWorkspace();
  const isMobileUX = useIsMobileUX();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!ws.notice) return;
    const message = takeWorkspaceNotice();
    if (message) notify.info(t("Back in your own account"), message);
  }, [ws.notice]);

  if (!ws.enabled || !ws.isShared) return null;

  const nav = navigation || navigationRef;
  const switchBack = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await switchWorkspace(null, { source: "banner" });
      if (!navigationRef || navigationRef.isReady?.()) {
        nav?.dispatch(
          CommonActions.reset({ index: 0, routes: [{ name: "EventList" }] })
        );
      }
    } finally {
      setBusy(false);
    }
  };

  const role = ws.role && ROLE_LABELS[ws.role];
  const name = <Text style={tw`font-semibold`}>{ws.name}</Text>;
  return (
    <View
      style={[
        tw`flex-row items-center px-4 py-2`,
        { backgroundColor: colors.ink, minHeight: 40 },
        style,
      ]}
    >
      <Ionicons
        name="people-outline"
        size={16}
        color={colors.white}
        style={tw`mr-2`}
      />
      <Text
        numberOfLines={1}
        style={[tw`flex-1 text-[13px] leading-[18px]`, { color: colors.white }]}
      >
        {isMobileUX
          ? tRich("Working in {team}", { team: name })
          : tRich("You're working in {team}'s account", { team: name })}
        {!!role && !isMobileUX ? ` · ${role}` : ""}
      </Text>
      <Pressable
        onPress={switchBack}
        accessibilityRole="button"
        accessibilityLabel={t("Switch to my account")}
        hitSlop={6}
        style={({ hovered, pressed }) => [
          tw`ml-3 px-2.5 py-1 rounded-md flex-row items-center`,
          (hovered || pressed) && { backgroundColor: "rgba(255,255,255,0.12)" },
        ]}
      >
        {busy && (
          <ActivityIndicator
            size="small"
            color={colors.white}
            style={tw`mr-1.5`}
          />
        )}
        <Text
          style={[
            tw`text-[13px] leading-[18px] font-semibold underline`,
            { color: colors.white },
          ]}
        >
          {isMobileUX ? t("My account") : t("Switch to my account")}
        </Text>
      </Pressable>
    </View>
  );
};

export default WorkspaceBanner;

// The web app's frame while you work in someone else's account (App.js): the
// banner sits over the top of the app like a status bar, and every screen
// below gets its height as a top inset (AppContainer pads by it; the wide
// layouts keep their centred frames below it). Your own account: just the app.
export const WorkspaceFrame = ({ navigationRef, children }) => {
  const ws = useWorkspace();
  const insets = useSafeAreaInsets();
  const [bannerHeight, setBannerHeight] = useState(40);
  const show = ws.enabled && ws.isShared;
  const value = useMemo(
    () => (show ? { ...insets, top: insets.top + bannerHeight } : insets),
    [insets, show, bannerHeight]
  );
  return (
    <View style={tw`flex-1`}>
      <SafeAreaInsetsContext.Provider value={value}>
        {children}
      </SafeAreaInsetsContext.Provider>
      {show && (
        <View
          style={[
            tw`absolute left-0 right-0`,
            { top: insets.top, zIndex: 20, elevation: 20 },
          ]}
          onLayout={(e) => {
            const height = Math.round(e.nativeEvent.layout.height);
            if (height && height !== bannerHeight) setBannerHeight(height);
          }}
        >
          <WorkspaceBanner navigationRef={navigationRef} />
        </View>
      )}
      {/* The once-only notice also shows when no banner is up. */}
      {!show && <WorkspaceNotice />}
    </View>
  );
};

// Shows the store's one-off notice as a toast (WorkspaceBanner does it while
// it's shown).
const WorkspaceNotice = () => {
  const ws = useWorkspace();
  useEffect(() => {
    if (!ws.notice) return;
    const message = takeWorkspaceNotice();
    if (message) notify.info(t("Back in your own account"), message);
  }, [ws.notice]);
  return null;
};
